import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import notificationService from '../../../services/notificationService';

const NotificationTab = () => {
  const { user, userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (user && userProfile) {
      fetchNotifications();
    }
  }, [user, userProfile]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // Get user's notification email
      const recipientEmail = userProfile?.notification_email || userProfile?.email;

      // Fetch notifications filtered by current user's ID or email
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select(`
          id,
          type,
          title,
          message,
          created_at,
          is_read,
          user_id,
          recipient_email
        `)
        ?.or(`user_id.eq.${user?.id},recipient_email.eq.${recipientEmail}`)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Transform notifications to display format
      const transformedNotifications = data?.map(notification => ({
        id: notification?.id,
        title: notification?.title,
        message: notification?.message,
        type: notification?.type || 'announcement',
        priority: 'medium', // Default priority since it's not in notifications table
        timestamp: new Date(notification?.created_at),
        read: notification?.is_read || false,
        recipients: notification?.user_id ? 'specific_user' : 'all_users'
      }));

      setNotifications(transformedNotifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      setDeletingId(notificationId);
      setOpenMenuId(null);

      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('id', notificationId);

      if (error) throw error;

      // Refresh notifications list
      await fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredNotifications = notifications?.filter(notification => {
    return filterType === 'all' || notification?.type === filterType;
  });

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'system': return 'Settings';
      case 'deadline': return 'Clock';
      case 'announcement': return 'Megaphone';
      case 'security': return 'Shield';
      case 'event': return 'Calendar';
      default: return 'Bell';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'system': return 'bg-blue-100 text-blue-600';
      case 'deadline': return 'bg-orange-100 text-orange-600';
      case 'announcement': return 'bg-purple-100 text-purple-600';
      case 'security': return 'bg-red-100 text-red-600';
      case 'event': return 'bg-green-100 text-green-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const CreateNotificationModal = () => {
    const [formData, setFormData] = useState({
      title: '',
      message: '',
      type: 'announcement',
      priority: 'medium',
      recipients: 'all_users'
    });
    const [sending, setSending] = useState(false);
    const [createError, setCreateError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (e) => {
      e?.preventDefault();
      setSending(true);
      setCreateError('');
      setSuccessMessage('');

      try {
        // 1. Save notification to database (removed priority field as it doesn't exist in notifications table)
        const { data: notification, error: notificationError } = await supabase
          ?.from('notifications')
          ?.insert({
            type: formData?.type,
            title: formData?.title,
            message: formData?.message,
            created_at: new Date()?.toISOString()
          })
          ?.select()
          ?.single();

        if (notificationError) throw notificationError;

        // 2. Get recipient emails based on selection
        let recipientEmails = [];
        
        if (formData?.recipients === 'all_users') {
          // Get all active emails from role_email_authorizations
          const { data: allEmails, error: emailError } = await supabase
            ?.from('role_email_authorizations')
            ?.select('email, full_name')
            ?.eq('is_active', true);
          
          if (emailError) {
            console.error('Error fetching all users:', emailError);
            throw new Error('Failed to fetch recipient emails from database');
          }
          
          console.log('📧 Fetched all users:', allEmails);
          recipientEmails = allEmails || [];
        } 
        else if (formData?.recipients === 'all_students') {
          // Get student emails from role_email_authorizations where role = 'student'
          const { data: studentEmails, error: emailError } = await supabase
            ?.from('role_email_authorizations')
            ?.select('email, full_name')
            ?.eq('role', 'student')
            ?.eq('is_active', true);
          
          if (emailError) {
            console.error('Error fetching students:', emailError);
            throw new Error('Failed to fetch student emails from database');
          }
          
          console.log('📧 Fetched students:', studentEmails);
          recipientEmails = studentEmails || [];
        }
        else if (formData?.recipients === 'city_officials') {
          // Get city official emails from role_email_authorizations where role = 'city_official'
          const { data: officialEmails, error: emailError } = await supabase
            ?.from('role_email_authorizations')
            ?.select('email, full_name')
            ?.eq('role', 'city_official')
            ?.eq('is_active', true);
          
          if (emailError) {
            console.error('Error fetching city officials:', emailError);
            throw new Error('Failed to fetch city official emails from database');
          }
          
          console.log('📧 Fetched city officials:', officialEmails);
          recipientEmails = officialEmails || [];
        }

        // 3. Validate that we have recipients from database query
        if (!recipientEmails || recipientEmails?.length === 0) {
          throw new Error(`No active ${formData?.recipients?.replace('_', ' ')} found in role_email_authorizations table. Please ensure users have been added to the system.`);
        }

        console.log(`📊 Total recipients fetched: ${recipientEmails?.length}`);

        // 4. Filter and validate email format - only check email validity, allow null full_name
        const validRecipients = recipientEmails?.filter(r => {
          // Check if email exists and is a valid string with @ symbol
          const hasValidEmail = r?.email && 
                                typeof r?.email === 'string' && 
                                r?.email?.trim()?.length > 0 &&
                                r?.email?.includes('@') &&
                                r?.email?.includes('.');
          
          if (!hasValidEmail) {
            console.warn(`⚠️ Invalid email for recipient:`, r);
          }
          
          // Only validate email - full_name can be null
          return hasValidEmail;
        });

        console.log(`✅ Valid recipients after filtering: ${validRecipients?.length}`);

        // 5. Final validation - ensure we have at least one valid recipient
        if (!validRecipients || validRecipients?.length === 0) {
          console.error('❌ No valid recipients after filtering. Raw data:', recipientEmails);
          throw new Error(
            `No valid email addresses found in ${formData?.recipients?.replace('_', ' ')}. ` +
            `Please check that:\n` +
            `1. Users exist in role_email_authorizations table\n` +
            `2. Email addresses are properly formatted (e.g., user@example.com)\n` +
            `3. is_active is set to true\n` +
            `Raw recipient count: ${recipientEmails?.length || 0}`
          );
        }

        console.log(`📧 Preparing to send emails to ${validRecipients?.length} recipients`);
        console.log('Sample recipient:', validRecipients?.[0]);

        // 6. Send email notifications to all recipients
        const emailResult = await notificationService?.sendNotificationEmails({
          recipients: validRecipients,
          subject: formData?.title,
          message: formData?.message,
          priority: formData?.priority,
          notificationType: formData?.type
        });

        if (!emailResult?.success) {
          console.error('❌ Email sending failed:', emailResult?.error);
          throw new Error(`Email sending failed: ${emailResult?.error || 'Unknown error from email service'}`);
        }

        // 7. Create individual notification records for each user (removed non-existent reference columns)
        const { data: userProfiles } = await supabase
          ?.from('user_profiles')
          ?.select('id, email')
          ?.in('email', validRecipients?.map(r => r?.email));

        if (userProfiles && userProfiles?.length > 0) {
          const userNotifications = userProfiles?.map(profile => ({
            user_id: profile?.id,
            type: formData?.type,
            title: formData?.title,
            message: formData?.message,
            created_at: new Date()?.toISOString()
          }));

          await supabase
            ?.from('notifications')
            ?.insert(userNotifications);
        }

        // Show success message
        setSuccessMessage(`✅ Notification sent successfully to ${validRecipients?.length} recipient(s)!`);
        
        // Refresh notifications list
        await fetchNotifications();
        
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowCreateModal(false);
          setSuccessMessage('');
          setFormData({
            title: '',
            message: '',
            type: 'announcement',
            priority: 'medium',
            recipients: 'all_users'
          });
        }, 2000);

      } catch (error) {
        console.error('❌ Error creating notification:', error);
        setCreateError(error?.message || 'Failed to create notification. Please try again.');
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Create Notification</h3>
            <button
              onClick={() => setShowCreateModal(false)}
              className="text-muted-foreground hover:text-foreground"
              disabled={sending}
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-success">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {createError && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-error" />
                <span className="text-sm text-error">{createError}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Title</label>
              <input
                type="text"
                required
                value={formData?.title}
                onChange={(e) => setFormData({...formData, title: e?.target?.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={sending}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Message</label>
              <textarea
                required
                rows={3}
                value={formData?.message}
                onChange={(e) => setFormData({...formData, message: e?.target?.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={sending}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Type</label>
                <select
                  value={formData?.type}
                  onChange={(e) => setFormData({...formData, type: e?.target?.value})}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={sending}
                >
                  <option value="announcement">Announcement</option>
                  <option value="system">System</option>
                  <option value="deadline">Deadline</option>
                  <option value="security">Security</option>
                  <option value="event">Event</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Priority</label>
                <select
                  value={formData?.priority}
                  onChange={(e) => setFormData({...formData, priority: e?.target?.value})}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  disabled={sending}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Recipients</label>
              <select
                value={formData?.recipients}
                onChange={(e) => setFormData({...formData, recipients: e?.target?.value})}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={sending}
              >
                <option value="all_users">All Users</option>
                <option value="all_students">All Students</option>
                <option value="city_officials">City Officials</option>
              </select>
              <p className="text-xs text-muted-foreground mt-1">
                {formData?.recipients === 'all_users' && 'Sends to all active users in role_email_authorization'}
                {formData?.recipients === 'all_students' && 'Sends to all students in role_email_authorization'}
                {formData?.recipients === 'city_officials' && 'Sends to all city officials in role_email_authorization'}
              </p>
            </div>
            <div className="flex space-x-3">
              <Button 
                type="submit" 
                variant="default" 
                className="flex-1"
                loading={sending}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Create & Send Notification'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="flex-1"
                disabled={sending}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
          <p className="text-muted-foreground">Manage system-wide notifications and announcements</p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateModal(true)}
        >
          Create Notification
        </Button>
      </div>
      {/* Filter */}
     {/*<div className="flex items-center space-x-4">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e?.target?.value)}
          className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">All Types</option>
          <option value="system">System</option>
          <option value="deadline">Deadline</option>
          <option value="announcement">Announcement</option>
          <option value="security">Security</option>
          <option value="event">Event</option>
        </select>
      </div>*/}
      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotifications?.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Icon name="Bell" size={48} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No notifications found</p>
            </div>
          ) : (
            filteredNotifications?.map((notification) => (
              <div
                key={notification?.id}
                className={`bg-card border rounded-lg p-4 hover:shadow-md transition-micro ${
                  !notification?.read ? 'border-l-4 border-l-primary' : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(notification?.type)}`}>
                      <Icon name={getTypeIcon(notification?.type)} size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h4 className="font-medium text-foreground">{notification?.title}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(notification?.priority)}`}>
                          {notification?.priority}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {notification?.message}
                      </p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>{notification?.timestamp?.toLocaleString()}</span>
                        <span>Type: {notification?.type}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!notification?.read && (
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                    )}
                    <div className="relative">
                      <button 
                        onClick={() => setOpenMenuId(openMenuId === notification?.id ? null : notification?.id)}
                        className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-gray-100"
                      >
                        <Icon name="MoreVertical" size={16} />
                      </button>
                      {openMenuId === notification?.id && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                          <button
                            onClick={() => handleDeleteNotification(notification?.id)}
                            disabled={deletingId === notification?.id}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 disabled:opacity-50"
                          >
                            <Icon name="Trash2" size={14} />
                            {deletingId === notification?.id ? 'Deleting...' : 'Delete notification'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* Notification Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Bell" size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {notifications?.length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Total Notifications</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="Send" size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {notifications?.filter(n => n?.read)?.length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Read</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {notifications?.filter(n => !n?.read)?.length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Unread</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {notifications?.filter(n => {
                  const today = new Date();
                  const notifDate = new Date(n?.timestamp);
                  return notifDate?.toDateString() === today?.toDateString();
                })?.length || 0}
              </p>
              <p className="text-sm text-muted-foreground">Today</p>
            </div>
          </div>
        </div>
      </div>
      {/* Create Notification Modal */}
      {showCreateModal && <CreateNotificationModal />}
    </div>
  );
};

export default NotificationTab;